import React, { useEffect, useMemo, useState } from "react";
import Shell from "@/components/layout/Shell";
import { PageHeader, PrimaryButton, GhostButton, EmptyState, StatusBadge, formatCOP, Field, inputCls } from "@/components/common";
import { api, downloadPdf } from "@/lib/api";
import { Plus, CreditCard, Send, Check, Trash2, RefreshCw, Receipt } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Pagos() {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id:"", amount:0, issued_at:"", due_at:"", concept:"Servicios de marketing" });
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("todos");

  const load = async () => {
    setLoading(true);
    try {
      const [i,c] = await Promise.all([api.get("/invoices"), api.get("/clients")]);
      setInvoices(i.data); setClients(c.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const clientMap = Object.fromEntries(clients.map(c => [c.client_id, c]));
  const filteredInvoices = invoices.filter(i => {
    const cli = clientMap[i.client_id];
    if (typeFilter === "mensual" && cli?.billing_type !== "mensual") return false;
    if (typeFilter === "unico" && cli?.billing_type !== "unico") return false;
    return true;
  });
  const now = new Date();
  const monthKey = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`;

  const summary = useMemo(() => {
    const monthInv = invoices.filter(i => i.issued_at.startsWith(monthKey));
    const totalIssued = monthInv.reduce((s,i)=>s+i.amount,0);
    const totalPaid = monthInv.filter(i=>i.status==="cobrada").reduce((s,i)=>s+i.amount,0);
    const totalPending = monthInv.filter(i=>i.status!=="cobrada").reduce((s,i)=>s+i.amount,0);
    return { totalIssued, totalPaid, totalPending };
  }, [invoices, monthKey]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.client_id) { toast.error("Selecciona un cliente"); return; }
    if (Number(form.amount) <= 0) { toast.error("El monto debe ser mayor a 0"); return; }
    try {
      await api.post("/invoices", { ...form, amount: Number(form.amount) });
      toast.success("Factura creada"); setOpen(false); load();
    } catch { toast.error("No se pudo crear la factura"); }
  };

  const markPaid = async (inv) => {
    await api.post(`/invoices/${inv.invoice_id}/mark-paid`);
    toast.success("Marcada como cobrada"); load();
  };

  const sendEmail = async (inv) => {
    const cli = clientMap[inv.client_id];
    if (!cli?.email) { toast.error("El cliente no tiene email configurado"); return; }
    try {
      await api.post(`/invoices/${inv.invoice_id}/send-email`);
      toast.success(`Factura enviada a ${cli.email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo enviar");
    }
  };

  const remove = async (inv) => {
    if (!window.confirm(`¿Eliminar factura ${inv.number}?`)) return;
    await api.delete(`/invoices/${inv.invoice_id}`);
    toast.success("Factura eliminada"); load();
  };

  const cuentaCobro = async (inv) => {
    try {
      const { data } = await api.post(`/invoices/${inv.invoice_id}/cuenta-cobro`);
      await downloadPdf(`/cuentas-cobro/${data.cc_id}/pdf`, `${data.number}.pdf`);
      toast.success(`Cuenta de cobro ${data.number} descargada`);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo generar la cuenta de cobro"); }
  };

  const generateRecurring = async () => {
    try {
      const { data } = await api.post("/invoices/generate-recurring");
      if (data.created > 0) toast.success(`${data.created} factura(s) recurrente(s) creadas`);
      else toast.info("Ya generadas las facturas de este mes");
      load();
    } catch { toast.error("No se pudieron generar"); }
  };

  const openNew = () => {
    const today = new Date().toISOString().slice(0,10);
    const due = new Date(Date.now()+15*24*3600*1000).toISOString().slice(0,10);
    setForm({ client_id: clients[0]?.client_id || "", amount: 0, issued_at: today, due_at: due, concept:"Servicios de marketing" });
    setOpen(true);
  };

  return (
    <Shell>
      <PageHeader
        title="Pagos"
        subtitle={`Facturación de ${now.toLocaleString("es-CO",{month:"long",year:"numeric"})}`}
        action={
          <div className="flex gap-2">
            <GhostButton data-testid="generate-recurring-btn" onClick={generateRecurring}>
              <RefreshCw className="w-4 h-4"/> Generar recurrentes
            </GhostButton>
            <PrimaryButton data-testid="add-invoice-btn" onClick={openNew}>
              <Plus className="w-4 h-4"/> Nueva factura
            </PrimaryButton>
          </div>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-[#15171E] border border-[#262A33] rounded-2xl p-5">
          <div className="text-xs text-[#9CA3AF] mb-2">Total facturado</div>
          <div className="text-2xl font-bold text-[#F5F5F7]" style={{ fontFamily: "Manrope, sans-serif" }}>{formatCOP(summary.totalIssued)}</div>
        </div>
        <div className="bg-[#15171E] border border-[#262A33] rounded-2xl p-5">
          <div className="text-xs text-[#9CA3AF] mb-2">Total cobrado</div>
          <div className="text-2xl font-bold text-emerald-400" style={{ fontFamily: "Manrope, sans-serif" }}>{formatCOP(summary.totalPaid)}</div>
        </div>
        <div className="bg-[#15171E] border border-[#262A33] rounded-2xl p-5">
          <div className="text-xs text-[#9CA3AF] mb-2">Total pendiente</div>
          <div className="text-2xl font-bold text-amber-400" style={{ fontFamily: "Manrope, sans-serif" }}>{formatCOP(summary.totalPending)}</div>
        </div>
      </div>

      {loading ? <div className="text-[#9CA3AF]">Cargando…</div> : invoices.length === 0 ? (
        <EmptyState icon={CreditCard} title="Aún no tienes facturas"
          description="Crea tu primera factura o genera automáticamente las recurrentes de tus clientes con retainer activo."
          action={<PrimaryButton onClick={openNew}><Plus className="w-4 h-4"/> Crear factura</PrimaryButton>}
        />
      ) : (
        <>
        <div className="mb-4 inline-flex items-center rounded-xl border border-[#262A33] bg-[#15171E] overflow-hidden">
          {[
            { key: "todos", label: "Todas" },
            { key: "mensual", label: "Mensuales" },
            { key: "unico", label: "Únicos (50/50)" },
          ].map((f, i) => (
            <button
              key={f.key}
              data-testid={`pay-filter-${f.key}`}
              onClick={()=>setTypeFilter(f.key)}
              className={`px-3 py-2 text-xs font-medium transition-colors ${i > 0 ? "border-l border-[#262A33]" : ""} ${
                typeFilter === f.key ? "bg-[#F97316]/10 text-[#F97316]" : "text-[#9CA3AF] hover:text-[#F5F5F7]"
              }`}
            >
              {f.label}
            </button>
          ))}
          <span className="px-3 py-2 text-[11px] text-[#6B7280] border-l border-[#262A33]">{filteredInvoices.length}</span>
        </div>
        <div className="bg-[#15171E] border border-[#262A33] rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#262A33] text-[#9CA3AF]">
                  <th className="text-left font-medium px-5 py-3">Número</th>
                  <th className="text-left font-medium px-5 py-3">Cliente</th>
                  <th className="text-left font-medium px-5 py-3">Emitida</th>
                  <th className="text-left font-medium px-5 py-3">Vence</th>
                  <th className="text-right font-medium px-5 py-3">Monto</th>
                  <th className="text-left font-medium px-5 py-3">Estado</th>
                  <th className="text-right font-medium px-5 py-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.map(inv => {
                  const c = clientMap[inv.client_id];
                  const badge = c?.billing_type === "unico"
                    ? <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-purple-500/10 text-purple-300 border border-purple-500/20 ml-1">U</span>
                    : <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-[#F97316]/10 text-[#F97316] border border-[#F97316]/20 ml-1">M</span>;
                  return (
                    <tr key={inv.invoice_id} data-testid={`invoice-row-${inv.invoice_id}`} className="border-b border-[#262A33] last:border-0 hover:bg-[#1A1D24] transition-colors">
                      <td className="px-5 py-3 text-[#F5F5F7] font-mono text-xs">{inv.number}</td>
                      <td className="px-5 py-3 text-[#F5F5F7]">{c?.name || "—"}{c && badge}</td>
                      <td className="px-5 py-3 text-[#9CA3AF]">{inv.issued_at}</td>
                      <td className="px-5 py-3 text-[#9CA3AF]">{inv.due_at}</td>
                      <td className="px-5 py-3 text-right text-[#F5F5F7] font-medium">{formatCOP(inv.amount)}</td>
                      <td className="px-5 py-3"><StatusBadge status={inv.status}/></td>
                      <td className="px-5 py-3 text-right">
                        <div className="inline-flex gap-1">
                          {inv.status !== "cobrada" && (
                            <button data-testid={`mark-paid-${inv.invoice_id}`} onClick={()=>markPaid(inv)} className="p-1.5 rounded-lg text-emerald-400 hover:bg-emerald-500/10 transition-colors" aria-label="Marcar cobrada" title="Marcar cobrada">
                              <Check className="w-4 h-4" strokeWidth={1.75}/>
                            </button>
                          )}
                          <button data-testid={`cuenta-cobro-${inv.invoice_id}`} onClick={()=>cuentaCobro(inv)} className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-[#F97316] hover:bg-[#F97316]/10 transition-colors" aria-label="Descargar cuenta de cobro" title="Descargar cuenta de cobro (PDF)">
                            <Receipt className="w-4 h-4" strokeWidth={1.75}/>
                          </button>
                          <button data-testid={`send-email-${inv.invoice_id}`} onClick={()=>sendEmail(inv)} className="p-1.5 rounded-lg text-[#F97316] hover:bg-[#F97316]/10 transition-colors" aria-label="Enviar por email" title="Enviar por email">
                            <Send className="w-4 h-4" strokeWidth={1.75}/>
                          </button>
                          <button onClick={()=>remove(inv)} className="p-1.5 rounded-lg text-[#6B7280] hover:text-red-400 hover:bg-red-500/10 transition-colors" aria-label="Eliminar">
                            <Trash2 className="w-4 h-4" strokeWidth={1.75}/>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#15171E] border-[#262A33] text-[#F5F5F7]">
          <DialogHeader><DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>Nueva factura</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <Field label="Cliente *">
              <select data-testid="invoice-client-select" required className={inputCls} value={form.client_id} onChange={e=>setForm({...form, client_id: e.target.value})}>
                <option value="">Selecciona un cliente</option>
                {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Concepto">
              <input className={inputCls} value={form.concept} onChange={e=>setForm({...form, concept: e.target.value})}/>
            </Field>
            <Field label="Monto (COP) *">
              <input data-testid="invoice-amount-input" required type="number" min="1" className={inputCls} value={form.amount} onChange={e=>setForm({...form, amount: e.target.value})}/>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Emitida"><input type="date" className={inputCls} value={form.issued_at} onChange={e=>setForm({...form, issued_at: e.target.value})}/></Field>
              <Field label="Vence"><input type="date" className={inputCls} value={form.due_at} onChange={e=>setForm({...form, due_at: e.target.value})}/></Field>
            </div>
            <DialogFooter>
              <GhostButton type="button" onClick={()=>setOpen(false)}>Cancelar</GhostButton>
              <PrimaryButton data-testid="save-invoice-btn" type="submit">Crear factura</PrimaryButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
